import React from 'react';
import dayjs from 'dayjs';
import { CircularProgress } from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import XtramModal, {
    XtramButton,
    XtramDatePicker,
    XtramField,
    XtramInput,
    XtramSelect,
    xtramStyles,
} from '../Widgets/XtramModal/XtramModal';

const isoDayToDate = (value) => (value ? dayjs(value).toDate() : null);
const dateToIsoDay = (date) => (date ? dayjs(date).format('YYYY-MM-DD') : '');
const sameEntry = (a, b) => String(a?.entryId) === String(b?.entryId);

/** Date-range picker before previewing transactions fetched from Finsite. Dates are "YYYY-MM-DD" strings. */
export const FinsiteFetchModal = ({ open, onClose, from, to, setFrom, setTo, onFetch, loading, direction }) => {
    const { t } = useTranslation();

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('FetchFromFinsite')}
            direction={direction}
            busy={loading}
            footer={
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={loading}>{t('Cancel')}</XtramButton>
                    <XtramButton onClick={onFetch} disabled={loading} busy={loading}>
                        <CloudSyncIcon fontSize="small" />
                        {t('Preview')}
                    </XtramButton>
                </>
            }
        >
            <div className={xtramStyles.formFields}>
                <XtramField label={t('FinsiteFromDate')}>
                    <XtramDatePicker value={isoDayToDate(from)} onChange={(date) => setFrom(dateToIsoDay(date))} />
                </XtramField>
                <XtramField label={t('FinsiteToDate')}>
                    <XtramDatePicker value={isoDayToDate(to)} onChange={(date) => setTo(dateToIsoDay(date))} />
                </XtramField>
            </div>
        </XtramModal>
    );
};

export const FinsiteSignupModal = ({ open, onClose, form, setForm, onSend, sending, direction }) => {
    const { t } = useTranslation();
    const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('FinsiteSignupRequest')}
            direction={direction}
            busy={sending}
            footer={
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={sending}>{t('Cancel')}</XtramButton>
                    <XtramButton onClick={onSend} disabled={sending} busy={sending}>
                        <CloudSyncIcon fontSize="small" />
                        {t('FinsiteSignupSend')}
                    </XtramButton>
                </>
            }
        >
            <div className={xtramStyles.section}>
                <p className={xtramStyles.muted}>{t('FinsiteSignupDialogIntro')}</p>
                <div className={xtramStyles.formFields}>
                    <XtramField label={t('Name')}>
                        <XtramInput direction={direction} value={form.accountantName} onChange={setField('accountantName')} />
                    </XtramField>
                    <XtramField label={t('AccountantId')}>
                        <XtramInput direction={direction} value={form.accountantId} InputProps={{ readOnly: true }} />
                    </XtramField>
                    <XtramField label={t('Email')}>
                        <XtramInput direction={direction} value={form.email} onChange={setField('email')} />
                    </XtramField>
                    <XtramField label={t('PhoneNumber')}>
                        <XtramInput direction={direction} value={form.phoneNumber} onChange={setField('phoneNumber')} />
                    </XtramField>
                </div>
            </div>
        </XtramModal>
    );
};

/** Links a Finsite company / bank account / booking account to the selected bank. */
export const FinsiteLinkModal = ({
    open,
    onClose,
    direction,
    subtitle,
    discovering,
    saving,
    companies,
    accounts,
    bookingAccounts,
    linkedBookingIds,
    formatBookingLabel,
    form,
    setForm,
    onSave,
}) => {
    const { t } = useTranslation();
    const findEntry = (options, entryId) => options.find((o) => String(o.entryId) === String(entryId)) || null;

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('FinsiteManualLink')}
            direction={direction}
            wide
            busy={saving}
            footer={
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={saving}>{t('Cancel')}</XtramButton>
                    <XtramButton onClick={onSave} disabled={saving || discovering} busy={saving}>
                        <LinkIcon fontSize="small" />
                        {t('FinsiteSaveLink')}
                    </XtramButton>
                </>
            }
        >
            <div className={xtramStyles.section}>
                <p className={xtramStyles.muted}>{subtitle}</p>
                {discovering ? (
                    <div className={xtramStyles.loading}><CircularProgress size={28} /></div>
                ) : (
                    <div className={xtramStyles.formFields}>
                        <XtramField label={t('FinsiteCompany')} size="full">
                            <XtramSelect
                                direction={direction}
                                options={companies}
                                value={findEntry(companies, form.companyEntryId)}
                                getOptionLabel={(o) => `${o.name || ''} (${o.taxRegistrationNumber || ''})`}
                                isOptionEqualToValue={sameEntry}
                                onChange={(val) => setForm({
                                    companyEntryId: val?.entryId ?? '',
                                    accountId: '',
                                    bookingAccountId: '',
                                })}
                            />
                        </XtramField>
                        <XtramField label={t('FinsiteAccount')} size="full">
                            <XtramSelect
                                direction={direction}
                                options={accounts}
                                value={findEntry(accounts, form.accountId)}
                                getOptionLabel={(o) => `${o.bank || ''} ${o.branch || ''}/${o.accountNumber || ''}`.trim()}
                                isOptionEqualToValue={sameEntry}
                                disabled={!form.companyEntryId}
                                onChange={(val) => setForm((prev) => ({
                                    ...prev,
                                    accountId: val?.entryId ?? '',
                                    bookingAccountId: '',
                                }))}
                            />
                        </XtramField>
                        <XtramField label={t('FinsiteBookingAccount')} size="full">
                            <XtramSelect
                                direction={direction}
                                options={bookingAccounts}
                                value={findEntry(bookingAccounts, form.bookingAccountId)}
                                getOptionLabel={(o) => {
                                    const base = formatBookingLabel(o);
                                    return linkedBookingIds.includes(String(o.entryId)) ? `${base} (${t('FinsiteLinked')})` : base;
                                }}
                                isOptionEqualToValue={sameEntry}
                                disabled={!form.accountId}
                                onChange={(val) => setForm((prev) => ({
                                    ...prev,
                                    bookingAccountId: val?.entryId ?? '',
                                }))}
                            />
                        </XtramField>
                    </div>
                )}
            </div>
        </XtramModal>
    );
};
